import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Editable,
  EditableInput,
  EditablePreview,
  EditableTextarea,
  Flex,
  Icon,
  Input,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Spacer,
  Tag,
  Text,
  Textarea,
  Tooltip,
  useBreakpointValue,
  useEditableControls,
  useToast,
} from '@chakra-ui/react'

import { useState } from 'react'
import { useAuth } from '../../../hooks/useAuth'
import { useFaqs, useCreateFaq, useUpdateFaq, useDeleteFaq } from '../../../hooks/useFaq'
import { useHideTabBar } from '../../../hooks/useHideTabBar'
import handleErrorToast from '../toast/handleErrorToast'
import handleSuccessToast from '../toast/handleSuccessToast'
import { CheckCircle, Edit, Trash, XCircle } from '../../../utils/constants/icons'

import { IconButton } from 'rsuite'
import InvalidInput from '../forms/InvalidInput'

const LinkifyText = ({ text, textStyle }: { text: string; textStyle: string }) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g
  const parts = text.split(urlRegex)

  return (
    <Text textStyle={textStyle} whiteSpace="pre-wrap">
      {parts.length > 0 &&
        parts.map((part, index) => {
          if (urlRegex.test(part)) {
            const url = new URL(part)
            const domain = url.hostname
            return (
              <Tooltip key={index} label={part} hasArrow>
                <Button variant="link" onClick={() => window.open(part, '_blank')} wordBreak="break-all">
                  {domain}
                </Button>
              </Tooltip>
            )
          } else {
            return part
          }
        })}
    </Text>
  )
}

interface FaqModalProps {
  isOpen: boolean
  onClose: () => void
}

const CustomEditableTextArea = ({
  text,
  onSubmit,
  onDelete,
}: {
  text: string
  onSubmit: (v: string) => void
  onDelete: () => void
}) => {
  const [value, setValue] = useState(text)
  const EditableControls = () => {
    const { isEditing, getSubmitButtonProps, getCancelButtonProps, getEditButtonProps } = useEditableControls()

    return (
      <Flex gap="10px" justifyContent="right">
        {isEditing ? (
          <>
            <IconButton
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              {...(getSubmitButtonProps() as any)}
              icon={
                <Icon w="15px" h="15px" viewBox="0 0 24 24" color="neutral.500" fill="none">
                  <CheckCircle />
                </Icon>
              }
            />
            <IconButton
              mr="40px"
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              {...(getCancelButtonProps() as any)}
              icon={
                <Icon w="15px" h="15px" viewBox="0 0 24 24" color="neutral.500" fill="none">
                  <XCircle />
                </Icon>
              }
            />
          </>
        ) : (
          <IconButton
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {...(getEditButtonProps() as any)}
            icon={
              <Icon w="15px" h="15px" viewBox="0 0 24 24" color="neutral.500" fill="none">
                <Edit />
              </Icon>
            }
          />
        )}
        <IconButton
          onClick={() => onDelete()}
          icon={
            <Icon w="18px" h="18px" viewBox="0 0 20 20" color="#E3502F" fill="none">
              <Trash />
            </Icon>
          }
        />
      </Flex>
    )
  }

  return (
    <Editable
      value={value}
      onChange={(v) => setValue(v)}
      onSubmit={(v) => onSubmit(v)}
      defaultValue={text}
      isPreviewFocusable={false}
      textStyle="b-sm"
      color="neutral.500"
    >
      {({ isEditing }) => (
        <>
          {isEditing ? (
            <>
              <EditableTextarea
                p="5px"
                _active={{ boxShadow: 'none' }}
                _focus={{ boxShadow: 'none' }}
                borderWidth="1px"
                borderColor="neutral.100"
                whiteSpace="pre-wrap"
              />
              <EditableControls />
            </>
          ) : (
            <>
              <LinkifyText text={value} textStyle="b-sm" />
              <EditableControls />
            </>
          )}
        </>
      )}
    </Editable>
  )
}

const CustomEditableInput = ({ text, onSubmit }: { text: string; onSubmit: (v: string) => void }) => {
  const [value, setValue] = useState(text)
  const EditableControls = () => {
    const { isEditing, getSubmitButtonProps, getCancelButtonProps, getEditButtonProps } = useEditableControls()

    return (
      <Flex gap="10px" justifyContent="left">
        {isEditing ? (
          <>
            <IconButton
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              {...(getSubmitButtonProps() as any)}
              icon={
                <Icon w="15px" h="15px" viewBox="0 0 24 24" color="neutral.500" fill="none">
                  <CheckCircle />
                </Icon>
              }
            />
            <IconButton
              mr="40px"
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              {...(getCancelButtonProps() as any)}
              icon={
                <Icon w="15px" h="15px" viewBox="0 0 24 24" color="neutral.500" fill="none">
                  <XCircle />
                </Icon>
              }
            />
          </>
        ) : (
          <IconButton
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {...(getEditButtonProps() as any)}
            icon={
              <Icon w="15px" h="15px" viewBox="0 0 24 24" color="neutral.500" fill="none">
                <Edit />
              </Icon>
            }
          />
        )}
      </Flex>
    )
  }

  return (
    <Editable
      value={value}
      onChange={(v) => setValue(v)}
      onSubmit={(v) => onSubmit(v)}
      defaultValue={text}
      isPreviewFocusable={false}
      textStyle="b-sm"
      color="black"
      display="flex"
      alignItems="center"
      justifyContent="left"
      gap="10px"
      mr="40px"
    >
      <EditablePreview />
      <EditableInput
        p="5px"
        _active={{
          boxShadow: 'none',
        }}
        _focus={{
          boxShadow: 'none',
        }}
        borderWidth="1px"
        borderColor="neutral.100"
      />
      <EditableControls />
    </Editable>
  )
}

const AddFaq = ({
  onSubmit,
  isLoading,
}: {
  onSubmit: (question: string, answer: string) => void
  isLoading: boolean
}) => {
  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState('')

  const [errors, setErrors] = useState({
    question: false,
    answer: false,
  })

  const handleQuestionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuestion(event.target.value)
  }

  const handleAnswerChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAnswer(event.target.value)
  }

  const handleSubmit = () => {
    if (validate()) {
      onSubmit(question, answer)
      setQuestion('')
      setAnswer('')
    }
  }

  const validate = () => {
    const errors = {
      question: question.length === 0,
      answer: answer.length === 0,
    }
    setErrors(errors)
    return !Object.values(errors).some((err) => err)
  }

  return (
    <Box>
      <Input
        type="text"
        value={question}
        onChange={handleQuestionChange}
        mb="5px"
        placeholder="Enter the question here..."
      />
      <InvalidInput isError={errors.question} label="question" />
      <Spacer h="20px" />

      <Textarea value={answer} onChange={handleAnswerChange} mb="5px" placeholder="Enter the answer here..." />
      <InvalidInput isError={errors.answer} label="answer" />
      <Spacer h="20px" />

      <Button colorScheme="heavy" onClick={handleSubmit} float="right" mb="10px" isLoading={isLoading}>
        Add FAQ
      </Button>
    </Box>
  )
}

const FaqModal: React.FC<FaqModalProps> = ({ isOpen, onClose }) => {
  const toast = useToast()
  const { permissions } = useAuth()

  const faqData = useFaqs()
  const createFaqMutation = useCreateFaq()
  const updateFaqMutation = useUpdateFaq()
  const deleteFaqMutation = useDeleteFaq()

  const canEdit = permissions.isAdmin
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false

  useHideTabBar(isOpen, 'faq-modal')

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: 'full', md: '4xl' }}
      scrollBehavior="inside"
      isCentered={!isMobile}
    >
      <ModalOverlay />
      <ModalContent
        m={{ base: 0, md: 'auto' }}
        borderRadius={{ base: 0, md: 'md' }}
        h={{ base: '100dvh', md: 'auto' }}
        maxH={{ base: '100dvh', md: '90vh' }}
        pt={{ base: 'calc(env(safe-area-inset-top) + 20px)', md: '24px' }}
        pb={{ base: 'calc(env(safe-area-inset-bottom) + 20px)', md: '24px' }}
        px={{ base: '20px', md: '24px' }}
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalCloseButton
          top={{ base: 'calc(env(safe-area-inset-top) + 12px)', md: '16px' }}
          right={{ base: '12px', md: '16px' }}
          w={{ base: '40px', md: '32px' }}
          h={{ base: '40px', md: '32px' }}
          borderRadius="7px"
          color="neutral.500"
          fontSize={{ base: '16px', md: '14px' }}
          zIndex="2"
          _hover={{
            bg: 'neutral.100',
            color: 'neutral.700',
          }}
        />

        <Flex
          alignItems={{ base: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          flexDirection={{ base: 'column', md: 'row' }}
          gap={{ base: '10px', md: '0' }}
          mb="10px"
          pr={{ base: '52px', md: '0' }}
        >
          <Text textStyle="h5" color="neutral.900">
            Frequently asked questions
          </Text>
          {canEdit && (
            <Tag bg="primary.50" color="primary.500" h="30px" flexShrink={0}>
              <Icon fill="none" w="16px" h="16px" viewBox="0 0 24 24" mr="5px">
                <CheckCircle />
              </Icon>
              As an admin you can edit this
            </Tag>
          )}
        </Flex>
        <Text textStyle="b-sm" fontWeight="400" color="neutral.500" mb={{ base: '20px', md: '40px' }}>
          Discover the Streamlined Efficiency of Ocean: Your Essential Tool for React Organization. Dive into Our FAQs
          for a Wealth of Solutions, Streamlining Your Workflow and Enhancing Productivity.
        </Text>

        <Accordion
          bg="neutral.10"
          p={{ base: '12px', md: '16px' }}
          borderRadius="12px"
          flex={{ base: '1 1 auto', md: '0 1 auto' }}
          minH={0}
          maxH={{ base: 'none', md: '500px' }}
          overflowY="auto"
          _focus={{}}
          _active={{}}
        >
          {(faqData.data ?? []).map((faq) => (
            <AccordionItem key={faq.id} borderTopColor="neutral.100">
              <AccordionButton
                _focus={{
                  boxShadow: 'none',
                }}
                _active={{}}
              >
                <Box as="span" flex="1" textAlign="left">
                  {canEdit ? (
                    <CustomEditableInput
                      text={faq.question}
                      onSubmit={(value) =>
                        updateFaqMutation.mutate(
                          { question: value, answer: faq.answer, id: faq.id },
                          {
                            onSuccess: () => handleSuccessToast(toast, 'Success!', 'The FAQ has been updated successfully'),
                            onError: () => handleErrorToast(toast, 'Error!', 'An error occurred while updating the FAQ'),
                          },
                        )
                      }
                    />
                  ) : (
                    <Text textStyle="b-sm" color="black" fontWeight="400">
                      {faq.question}
                    </Text>
                  )}
                </Box>
                <AccordionIcon color="neutral.400" />
              </AccordionButton>
              <AccordionPanel pb={4}>
                {canEdit ? (
                  <CustomEditableTextArea
                    text={faq.answer}
                    onDelete={() =>
                      deleteFaqMutation.mutate(faq.id, {
                        onSuccess: () => handleSuccessToast(toast, 'Success!', 'The FAQ has been deleted successfully'),
                        onError: () => handleErrorToast(toast, 'Error!', 'An error occurred while deleting the FAQ'),
                      })
                    }
                    onSubmit={(value) =>
                      updateFaqMutation.mutate(
                        { question: faq.question, answer: value, id: faq.id },
                        {
                          onSuccess: () => handleSuccessToast(toast, 'Success!', 'The FAQ has been updated successfully'),
                          onError: () => handleErrorToast(toast, 'Error!', 'An error occurred while updating the FAQ'),
                        },
                      )
                    }
                  />
                ) : (
                  <LinkifyText text={faq.answer} textStyle="b-sm" />
                )}
              </AccordionPanel>
            </AccordionItem>
          ))}

          <AccordionItem>
            <AccordionButton
              _focus={{
                boxShadow: 'none',
              }}
              _active={{}}
            >
              <Box as="span" flex="1" textAlign="left">
                <Text textStyle="b-sm" color="black" fontWeight="400">
                  Add a frequently asked question
                </Text>
              </Box>
              <AccordionIcon color="neutral.400" />
            </AccordionButton>
            <AccordionPanel pb={4}>
              <AddFaq
                isLoading={createFaqMutation.isPending}
                onSubmit={(q, a) =>
                  createFaqMutation.mutate(
                    { question: q, answer: a },
                    {
                      onSuccess: () => handleSuccessToast(toast, 'Success!', 'The FAQ has been created successfully'),
                      onError: () => handleErrorToast(toast, 'Error!', 'An error occurred while creating the FAQ'),
                    },
                  )
                }
              />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>

        <Flex
          gap={{ base: '12px', md: '10px' }}
          mt={{ base: '16px', md: '20px' }}
          flexDirection={{ base: 'column', md: 'row' }}
          alignItems={{ base: 'stretch', md: 'center' }}
          justifyContent="space-between"
          flexShrink={0}
        >
          <Text textStyle="b-sm" fontWeight="400" color="neutral.900" flexShrink={0}>
            Need more help?
          </Text>
          <Text
            textStyle="b-sm"
            fontWeight="400"
            color="neutral.900"
            textAlign={{ base: 'left', md: 'center' }}
            wordBreak="break-word"
          >
            Contact our support team at
            <Button
              variant="link"
              m="0 4px"
              as="a"
              href="mailto:ocean@react.org"
              _focus={{
                boxShadow: 'none',
              }}
              _active={{}}
            >
              ocean@react.org
            </Button>
            or check out our help center
          </Text>
          <Button
            w={{ base: '100%', md: 'auto' }}
            h={{ base: '48px', md: 'auto' }}
            flexShrink={0}
            onClick={() => window.open(`${window.location.origin}/#/help`, '_blank')}
          >
            Go to Help Center
          </Button>
        </Flex>
      </ModalContent>
    </Modal>
  )
}

export default FaqModal
